import React, { useMemo } from 'react';

function fmtSecs(secs) {
  if (!secs && secs !== 0) return '—';
  const s = Math.round(secs);
  return `${s}`;
}

function fmtTotalSecs(secs) {
  if (!secs && secs !== 0) return '0 minutes';
  const m = Math.floor(secs / 60);
  if (m === 0) return `${Math.round(secs)}s`;
  return `${m} minute${m === 1 ? '' : 's'}`;
}

export default function SessionSummary({
  questions,
  answers,
  totalTimeSeconds,
  onStartAnother,
  onReviewIncorrect,
}) {
  const totalCorrect = answers.filter((a) => a && a.correct).length;
  const total = answers.length;
  const pct = total > 0 ? Math.round((totalCorrect / total) * 100) : 0;

  const avgTime = useMemo(() => {
    const times = answers.map((a) => a && a.response_time_seconds).filter((t) => t != null && t > 0);
    if (!times.length) return null;
    return times.reduce((s, t) => s + t, 0) / times.length;
  }, [answers]);

  const hasIncorrect = answers.some((a) => a && !a.correct);

  const scoreColour = pct >= 70 ? 'text-green' : pct >= 50 ? 'text-amber' : 'text-red';

  return (
    <div>
      <div className="text-center mb-sp-6">
        <h2 className="text-2xl font-bold text-text-primary mb-sp-1">Session complete</h2>
        <p className="text-sm text-text-muted">
          {total} question{total === 1 ? '' : 's'} · {fmtTotalSecs(totalTimeSeconds)}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-sp-3 mb-sp-6">
        <div className="bg-bg-card rounded-r-xl border border-border-soft px-sp-4 py-sp-4 text-center">
          <p className="text-xs font-medium text-text-muted mb-sp-1">Score</p>
          <p className={`text-[32px] font-bold ${scoreColour}`}>
            {totalCorrect}
            <span className="text-base font-normal text-text-muted"> / {total}</span>
          </p>
        </div>
        <div className="bg-bg-card rounded-r-xl border border-border-soft px-sp-4 py-sp-4 text-center">
          <p className="text-xs font-medium text-text-muted mb-sp-1">Accuracy</p>
          <p className={`text-[32px] font-bold ${scoreColour}`}>{pct}%</p>
        </div>
        <div className="bg-bg-card rounded-r-xl border border-border-soft px-sp-4 py-sp-4 text-center">
          <p className="text-xs font-medium text-text-muted mb-sp-1">Avg. time</p>
          <p className="text-[32px] font-bold text-text-primary">
            {avgTime != null ? fmtSecs(avgTime) : '—'}
            <span className="text-sm font-normal text-text-muted">s</span>
          </p>
        </div>
      </div>

      <div className="bg-bg-card rounded-r-xl border border-border-soft mb-sp-6">
        <div className="px-sp-6 py-sp-3 border-b border-border-soft text-[15px] font-semibold text-text-primary">
          Question breakdown
        </div>
        {questions.map((q, i) => {
          const ans = answers[i];
          if (!ans) return null;
          const isCorrect = ans.correct;
          const topicName = q.subtopic || q.general_topic || 'Question ' + (i + 1);
          const secs = ans.response_time_seconds;
          return (
            <div
              key={i}
              className="px-sp-5 py-sp-3 flex items-center gap-sp-3 border-b border-border-soft last:border-b-0"
            >
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  isCorrect ? 'bg-green' : 'bg-red'
                }`}
              />
              <span className="text-sm text-text-primary flex-1 truncate">{topicName}</span>
              <span
                className={`text-xs font-medium whitespace-nowrap ${
                  isCorrect ? 'text-green' : 'text-red'
                }`}
              >
                {isCorrect ? 'Correct' : 'Incorrect'}
                {secs != null ? ` · ${Math.round(secs)}s` : ''}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex justify-center gap-sp-3 mb-sp-12">
        {hasIncorrect && (
          <button
            onClick={onReviewIncorrect}
            className="px-sp-5 py-sp-3 text-sm font-medium bg-transparent text-text-secondary border border-border-soft rounded-r-md hover:bg-bg-page transition-colors"
          >
            Review wrong answers
          </button>
        )}
        <button
          onClick={onStartAnother}
          className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors active:scale-[0.98]"
        >
          Start new session
        </button>
      </div>
    </div>
  );
}
